/* eslint-disable react/no-array-index-key */

import Alert from '@mui/material/Alert';
/*
 * TextWithFeatures
 *
 * Renders the text with links and new lines converted to HTML. Add more features here (deep links, etc.)
 *
 */
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { legalDisclaimer } from '../aiConfig';
import LinkButton from './LinkButton.tsx';

/*
 * LinkRenderer lets us control exactly how links are generated (<a> tags) in the markdown.
 */
const LinkRenderer = ({ node, ...props }) => (
  <a {...props} target='_blank' rel='noopener noreferrer'>
    {props.children}
  </a>
);

LinkRenderer.propTypes = {
  node: PropTypes.object,
  children: PropTypes.node.isRequired,
};

const keyStr = (str) => str.slice(-25);
/*
 * ParagraphRenderer provides for custom markdown capabilities like follow-up buttons
 */
const ParagraphRenderer = ({ children, followUpFunction, chatIcon }) => {
  const childArray = React.Children.toArray(children);
  const processedChildren = [];
  let followupStarted = false;

  childArray.forEach((child, childIndex) => {
    if (typeof child === 'string') {
      // Split the string by [follow-up] tags
      const parts = child.split(/(?=\[(?:follow-up|disclaimer-needed)\])/g);

      // console.log("PARTS ARE ", parts);
      parts.forEach((part, index) => {
        if (part.startsWith('[follow-up]')) {
          const followUpText = part.replace('[follow-up]', '').trim();
          if (!followupStarted) {
            followupStarted = true;
            processedChildren.push(
              <Divider key={`pgph-divider-${childIndex}-${index}-${keyStr(part)}`} sx={{ marginY: 1 }} />,
            );
          }
          processedChildren.push(
            <LinkButton key={`pgph-followup-${childIndex}-${index}`} onClick={() => followUpFunction(followUpText)}>
              test {followUpText}
            </LinkButton>,
          );
        } else if (part.startsWith('[disclaimer-needed]')) {
          part &&
            processedChildren.push(
              <Alert severity='info' key={`partd2${index}-${childIndex}-${keyStr(part)}`} sx={{ marginY: 1 }}>
                <b>{`Legal Disclaimer:`} </b>
                {legalDisclaimer} <span> {part.slice(19)} </span>{' '}
              </Alert>,
            );
        } else {
          if (chatIcon) processedChildren.push(<span key={`icon${index}`}> {chatIcon} </span>);
          part && processedChildren.push(<span key={`part-${childIndex}-${index}`}> {part} </span>);
        }
      });
    } else {
      processedChildren.push(child);
    }
  });

  return <>{processedChildren}</>;
};

ParagraphRenderer.propTypes = {
  children: PropTypes.node.isRequired,
  followUpFunction: PropTypes.func.isRequired,
  chatIcon: PropTypes.any,
};

export default function TextWithFeatures({ text, followUpFunction, chatIcon }) {
  return (
    <ReactMarkdown
      rehypePlugins={[rehypeRaw]}
      components={{
        a: LinkRenderer,
        p: (props) => (
          <Typography component='div' variant='paragraphRegular'>
            <ParagraphRenderer {...props} chatIcon={chatIcon} followUpFunction={followUpFunction} />
          </Typography>
        ),

        ul: (props) => (
          <Typography component='ul' variant='paragraphRegular' style={{ paddingLeft: 1, paddingInlineStart: 1 }}>
            <ul {...props} />
          </Typography>
        ),
        ol: (props) => (
          <Typography component='ol' variant='paragraphRegular' style={{ paddingLeft: 0.5, paddingInlineStart: 1 }}>
            <ol {...props} />
          </Typography>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

TextWithFeatures.propTypes = {
  text: PropTypes.string.isRequired,
  followUpFunction: PropTypes.func.isRequired,
  chatIcon: PropTypes.any,
};
